import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import MDBox from "@vendor/components/MDBox";
import { InputController } from "@shared/ui/form";
import Loading from "@vendor/examples/loading";
import { chargeHourPublic } from "../api/timeEntryApi";
import { sumHours, isFullDay } from "../lib/hourRules";
import { FULL_WEEK_HOURS, HOURS_PER_DAY } from "../constants";

// Valores del form: los 4 InputController son type="number" pero react-hook-form
// no castea sin resolver (no hay yup acá), así que llegan como string tal cual el
// usuario los tipea.
interface ChargeHourFormValues {
  hourProductive: string;
  hourVacation: string;
  hourDisease: string;
  hourOther: string;
}

/** Forma del error de axios que este form lee para mostrar el mensaje del backend. */
interface ApiError {
  response?: { data?: { message?: string } };
}

// Formulario de carga manual de horas para el link público de imputación.
// Se muestra cuando el tipo de carga NO es automático. El total y los errores
// son DERIVADOS de los valores observados; las reglas viven en lib/hourRules.
export const ChargeHourForm = () => {
  const { t } = useTranslation();
  const { id, token, projectName, localDate, type } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<ChargeHourFormValues>({ mode: "onChange" });

  const rulesDefault = { required: true, max: FULL_WEEK_HOURS };

  const hourProductive = watch("hourProductive");
  const hourVacation = watch("hourVacation");
  const hourDisease = watch("hourDisease");
  const hourOther = watch("hourOther");

  const totalHour = sumHours(hourProductive, hourVacation, hourDisease, hourOther);

  // Cada categoría debe ser una jornada completa (múltiplo de HOURS_PER_DAY).
  const errorInput = {
    hourProductive: !isFullDay(hourProductive),
    hourVacation: !isFullDay(hourVacation),
    hourDisease: !isFullDay(hourDisease),
    hourOther: !isFullDay(hourOther),
  };
  const isValidForm = () => Object.values(errorInput).every((hasError) => !hasError);

  const onSubmit = (value: ChargeHourFormValues) => {
    if (totalHour !== FULL_WEEK_HOURS) {
      Swal.fire(
        t("chargeFormInvalidTotalTitle"),
        t("chargeFormInvalidTotalText", { hours: FULL_WEEK_HOURS }),
        "info"
      ).then();
      return;
    }
    if (!isValidForm()) return;

    setIsLoading(true);
    const request = {
      id,
      token,
      projectName,
      localDate,
      type,
      hourProductive: value.hourProductive,
      hourVacation: value.hourVacation,
      hourDisease: value.hourDisease,
      hourOther: value.hourOther,
    };
    chargeHourPublic(request)
      .then(() => {
        setIsLoading(false);
        // Hallazgo nuevo (no en #2572): `.then(reset())` invoca `reset()` de inmediato (su
        // valor de retorno, `void`, es lo que se le pasaba a `.then`, que en los hechos no
        // hacía nada) en vez de esperar a que el usuario cierre el alert. TS no tipa `void`
        // como callback válido de `.then`, así que se preserva el orden exacto de ejecución
        // preexistente (Swal.fire se invoca, reset() corre ya) sin encadenar un `.then` inerte.
        Swal.fire(t("chargeFormSuccessTitle"), t("chargeFormSuccessText"), "success");
        reset();
      })
      .catch((error: ApiError) => {
        setIsLoading(false);
        Swal.fire(
          t("chargeFormErrorTitle"),
          error.response?.data?.message ?? t("internalError"),
          "error"
        ).then();
      });
  };

  const invalidDayMessage = t("chargeFormInvalidDay", { hours: HOURS_PER_DAY });
  const maxValueMessage = t("chargeFormMaxValue", { max: FULL_WEEK_HOURS });

  return (
    <Box style={{ paddingBottom: 20 }}>
      <Loading open={isLoading} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography variant="h4">{t("chargeFormTitle")}</Typography>
            </Box>
          </Grid>
          <Grid size={12}>
            <MDBox ml={2} mr={2}>
              <InputController
                control={control}
                name="hourProductive"
                defaultValue="0"
                label={t("chargeFormProductiveLabel")}
                rules={rulesDefault}
                type="number"
              />
              {errorInput.hourProductive && (
                <span style={{ fontSize: 12, color: "red" }}>{invalidDayMessage}</span>
              )}
              {errors.hourProductive?.type === "max" && (
                <span style={{ fontSize: 12, color: "red" }}>{maxValueMessage}</span>
              )}
            </MDBox>
          </Grid>
          <Grid size={12}>
            <MDBox mt={-1} ml={2} mr={2}>
              <InputController
                control={control}
                name="hourVacation"
                defaultValue="0"
                label={t("chargeFormVacationLabel")}
                rules={rulesDefault}
                type="number"
              />
              {errorInput.hourVacation && (
                <span style={{ fontSize: 12, color: "red" }}>{invalidDayMessage}</span>
              )}
              {errors.hourVacation?.type === "max" && (
                <span style={{ fontSize: 12, color: "red" }}>{maxValueMessage}</span>
              )}
            </MDBox>
          </Grid>
          <Grid size={12}>
            <MDBox mt={-1} ml={2} mr={2}>
              <InputController
                control={control}
                name="hourDisease"
                defaultValue="0"
                label={t("chargeFormDiseaseLabel")}
                rules={rulesDefault}
                type="number"
              />
              {errorInput.hourDisease && (
                <span style={{ fontSize: 12, color: "red" }}>{invalidDayMessage}</span>
              )}
              {errors.hourDisease?.type === "max" && (
                <span style={{ fontSize: 12, color: "red" }}>{maxValueMessage}</span>
              )}
            </MDBox>
          </Grid>
          <Grid size={12}>
            <MDBox mt={-1} ml={2} mr={2}>
              <InputController
                control={control}
                name="hourOther"
                defaultValue="0"
                label={t("chargeFormOtherLabel")}
                rules={rulesDefault}
                type="number"
              />
              {errorInput.hourOther && (
                <span style={{ fontSize: 12, color: "red" }}>{invalidDayMessage}</span>
              )}
              {errors.hourOther?.type === "max" && (
                <span style={{ fontSize: 12, color: "red" }}>{maxValueMessage}</span>
              )}
            </MDBox>
          </Grid>
          <Grid size={12}>
            <Box
              sx={{ display: "flex", justifyContent: "end" }}
              style={{ marginLeft: 20, marginRight: 20 }}
            >
              <Typography variant="h6">
                {t("chargeFormTotalLabel")}: {totalHour}
              </Typography>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box
              sx={{ display: "flex", justifyContent: "end" }}
              style={{ marginLeft: 20, marginRight: 20 }}
            >
              <Button
                style={{ color: "white", backgroundColor: "black" }}
                type="submit"
                variant="contained"
                fullWidth
              >
                {t("chargeFormSubmit")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
